// src/pages/staff/offices/support-records/index.tsx
import { useEffect, useState } from 'react';
import { Heading } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getAttendanceReservationsWithLineID } from '@/api/getAttendanceReservations';
import { getLineUsers } from '@/api/getLineUsers';
import {
  getLineSupportRecordByReservationID,
  updateStaffSupportRecord,
  createStaffSupportRecord,
} from '@/api/getSupportRecord';
import { getStaffTemplates } from '@/api/getTemplates';
import SupportRecordSection from '@/components/Staff/SupportRecordSection';
import { LineUserAtom } from '@/jotai/jotai';
import { fileToBase64 } from '@/logics/base64/base64';
import { formattedDate } from '@/logics/date/date';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';
import { SupportRecord, UpdateSupportRecordInput } from '@/types/supportRecord';
import { LineAccountUser } from '@/types/user';

export const StaffSupportRecordsPage = () => {
  const [lineUser] = useAtom(LineUserAtom);
  const [displayDate, setDisplayDate] = useState<string>();
  const [lineAccountUser, setLineAccountUser] = useState<LineAccountUser | null>(null);

  useEffect(() => {
    setDisplayDate(formattedDate(new Date(), 'yyyy年M月dd日(EEE)'));
    const fetchLineAccountUser = async () => {
      if (!lineUser?.lineID) return;
      const { status, result } = await getLineUsers();
      if (status === 200) {
        const matched = result.find((user) => user.line_id === lineUser.lineID);
        setLineAccountUser(matched ?? null);
      }
    };

    fetchLineAccountUser();
  }, [lineUser]);

  const [records, setRecords] = useState<SupportRecord[]>([]);
  const [recordExistMap, setRecordExistMap] = useState<Record<number, boolean>>({});
  const [editedRecords, setEditedRecords] = useState<
    Record<number, Partial<SupportRecord> & { imageFiles?: File[] }>
  >({});

  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { office_id } = useParams();
  const [conditionTemplates, setConditionTemplates] = useState<
    { id: number; body: string; category: string }[]
  >([]);

  const jstDate = new Date()
    .toLocaleDateString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '-');
  console.log('jstDate', jstDate);

  useEffect(() => {
    setDisplayDate(formattedDate(new Date(), 'yyyy年M月dd日(EEE)'));
    if (office_id && lineUser) {
      fetchRecords(jstDate);
      fetchTemplates();
    }
  }, [lineUser, office_id]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const fetchRecords = async (dateJST: string) => {
    const Res = await getAttendanceReservationsWithLineID(
      lineUser?.lineID ?? '',
      lineUser?.accessToken ?? '',
      office_id ?? '0',
      dateJST
    );

    if (Res.status !== 200) return;

    const fetchedRecords: SupportRecord[] = [];
    const recordExistMap: Record<number, boolean> = {};

    for (const reservation of Res.result as DetailedAttendanceReservation[]) {
      const res = await getLineSupportRecordByReservationID(
        reservation.id,
        lineUser?.accessToken ?? ''
      );

      if (res.status === 200) {
        fetchedRecords.push(res.result);
        recordExistMap[reservation.id] = true;
      } else if (res.status === 404) {
        const emptyRecord: SupportRecord = {
          reservation_id: reservation.id,
          recorder: {
            id: lineAccountUser?.staff_id ?? 0,
            name: `${lineAccountUser?.staff_name ?? ''}`,
            role: 'staff',
          },
          child: reservation.child ?? { last_name: '', first_name: '' },
          date: reservation.date,
          start_time: reservation.start_time ?? '',
          end_time: reservation.end_time ?? '',
          attendance_status: reservation.attendance_status ?? 'attend',
          absent_date: reservation.absent_date ?? '',
          absent_reason_by_guardian: reservation.absent_reason ?? '',
          absent_reason_by_staff: '',
          is_rescheduled: false,
          rescheduled_date: '',
          is_absent_addition: false,
          pickup_or_arrival_time:
            reservation.pickup_info?.isRequired === true
              ? (reservation.pickup_info?.pickup_time ?? '')
              : (reservation.pickup_info?.arrival_time ?? ''),
          is_pickup_addition: false,
          dropoff_or_departure_time:
            reservation.dropoff_info?.isRequired === true
              ? (reservation.dropoff_info?.dropoff_time ?? '')
              : (reservation.dropoff_info?.departure_time ?? ''),
          is_dropoff_addition: false,
          body_temperature: 0,
          condition: '',
          support_content: '',
          staff_message: '',
          staff_report: '',
          guardian_message: '',
          support_record_images: [],
          created_at: '',
          updated_at: '',
        };
        fetchedRecords.push(emptyRecord);
        recordExistMap[reservation.id] = false;
      }
    }

    setRecords(fetchedRecords);
    setRecordExistMap(recordExistMap);
  };

  const fetchTemplates = async () => {
    try {
      const { status, result } = await getStaffTemplates(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? ''
      );
      if (status === 200 && result.length > 0) {
        setConditionTemplates(result);
      }
    } catch (error) {
      console.error('テンプレートの取得に失敗しました', error);
    }
  };

  const handleFieldChange = (
    recordId: number,
    field: keyof SupportRecord | 'imageFiles',
    value: string | number | File[]
  ) => {
    setEditedRecords((prev) => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        [field]: value,
      },
    }));
    setIsDirty(true);
  };

  // 統一されたスタッフ側保存処理
  const handleSaveAll = async () => {
    setIsSubmitting(true);
    try {
      for (const record of records) {
        const edited = editedRecords[record.reservation_id];
        if (!edited) continue;

        const imageFiles = edited.imageFiles || [];
        const base64Images = await Promise.all(imageFiles.map(fileToBase64));

        const userEmail = localStorage.getItem('email');
        if (!userEmail) {
          console.error('メールアドレスが取得できません');
          return;
        }

        const isAbsent = (edited.attendance_status || record.attendance_status) === 'absent';

        const payload: UpdateSupportRecordInput = {
          recorder_email: userEmail,
          attendance_status: edited.attendance_status ?? record.attendance_status,
          absent_date: edited.absent_date || record.absent_date || undefined,
          absent_reason_by_guardian:
            edited.absent_reason_by_guardian ?? record.absent_reason_by_guardian,
          absent_reason_by_staff: edited.absent_reason_by_staff ?? record.absent_reason_by_staff,
          is_rescheduled: edited.is_rescheduled ?? record.is_rescheduled,
          rescheduled_date: edited.rescheduled_date || record.rescheduled_date || undefined,
          is_absent_addition: edited.is_absent_addition ?? record.is_absent_addition,
          pickup_or_arrival_time: isAbsent
            ? ''
            : (edited.pickup_or_arrival_time ?? record.pickup_or_arrival_time),
          is_pickup_addition: edited.is_pickup_addition ?? record.is_pickup_addition,
          dropoff_or_departure_time: isAbsent
            ? ''
            : (edited.dropoff_or_departure_time ?? record.dropoff_or_departure_time),
          is_dropoff_addition: edited.is_dropoff_addition ?? record.is_dropoff_addition,
          body_temperature: isAbsent ? 0 : (edited.body_temperature ?? record.body_temperature),
          condition: isAbsent ? '' : (edited.condition ?? record.condition),
          support_content: isAbsent ? '' : (edited.support_content ?? record.support_content),
          staff_message: edited.staff_message ?? record.staff_message,
          staff_report: edited.staff_report ?? record.staff_report,
          support_record_images: base64Images,
          reservation_id: record.reservation_id,
        };

        const isExisting = recordExistMap[record.reservation_id];
        const result = isExisting
          ? await updateStaffSupportRecord(
              lineUser?.lineID ?? '',
              lineUser?.accessToken ?? '',
              office_id ?? '0',
              record.reservation_id,
              payload
            )
          : await createStaffSupportRecord(
              lineUser?.lineID ?? '',
              lineUser?.accessToken ?? '',
              office_id ?? '0',
              record.reservation_id,
              payload
            );

        if (result.status !== 200 && result.status !== 201) {
          alert(`保存に失敗しました（ID: ${record.reservation_id}）`);
          setIsSubmitting(false);
          return;
        }
      }

      window.location.reload();
    } catch (error) {
      console.error('送信中にエラーが発生しました:', error);
      alert('送信に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="m-4 space-y-4 pb-24">
      <Heading className="text-2xl font-bold">支援記録（実績記録）</Heading>

      {records.length === 0 && <div className="text-lg">本日の支援記録はありません</div>}

      {lineAccountUser &&
        records.map((record) => (
          <SupportRecordSection
            key={record.reservation_id}
            record={record}
            displayDate={displayDate}
            editedValues={editedRecords[record.reservation_id] ?? {}}
            onFieldChange={(field, value) => handleFieldChange(record.reservation_id, field, value)}
            conditionTemplates={conditionTemplates}
            lineUser={lineAccountUser}
          />
        ))}

      <div className="fixed bottom-0 left-0 right-0 space-y-2 bg-white p-4">
        <button
          className="w-full rounded bg-blue-700 py-3 text-base text-white"
          onClick={handleSaveAll}
          disabled={isSubmitting}
        >
          {isSubmitting ? '送信中...' : '送信'}
        </button>
        <button
          className="block w-full py-2 text-center text-base text-blue-700 underline"
          onClick={() => window.history.back()}
        >
          戻る
        </button>
      </div>
    </div>
  );
};
